import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Alert
} from '@material-ui/core';
import PropTypes from 'prop-types';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import useVrackApi from '../../../hooks/useVrackApi';
import SnapshotName from './SnapshotName';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  p: 4
};

const emptyForm = {
  rackName: '',
  snapshotName: '',
  description: '',
  identity: ''
};

function hasValue(value) {
  return value.trim().length > 0;
}

CreateSnapshotModal.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  rackName: PropTypes.string,
  identity: PropTypes.string
};

export default function CreateSnapshotModal({ isOpen, closeHandler, rackName, identity }) {
  const [newForm, setNewForm] = useState(emptyForm);
  const [isSnapshotNameValid, setIsSnapshotNameValid] = useState(false);

  const { fetchData: vrackApiCreateSnapshot } = useVrackApi('post', '/racks/snapshot');

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));

  const setField = (field) => (event) => {
    setNewForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSnapshotNameValidationChange = (isValid) => {
    setIsSnapshotNameValid(isValid);
  };
  const formValid =
    hasValue(newForm.rackName) && hasValue(newForm.snapshotName) && hasValue(newForm.description);

  useEffect(() => {
    if (isOpen) {
      setNewForm({
        rackName,
        snapshotName: '',
        description: '',
        identity
      });
    }
  }, [isOpen, rackName, identity]);

  const handleSubmit = async () => {
    try {
      await vrackApiCreateSnapshot({ data: newForm });
      openSnackbar({ message: 'Snapshot request submitted successfully!' });
      closeHandler();
    } catch (error) {
      openSnackbar({
        message: `Fail: ${error?.response?.data?.message || 'Fail to create snapshot'}`,
        severity: 'error'
      });
    }
  };

  return (
    <Modal
      data-testid="create-snapshot-modal"
      open={isOpen}
      sx={{ outline: 'none' }}
      onClose={closeHandler}
      aria-labelledby="create-snapshot-modal"
      aria-describedby="modal to create a snapshot"
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <Grid direction="row" sx={style} container>
          <Grid item xs={12} md={1} lg={2} onClick={closeHandler} />
          <Grid item xs={12} md={10} lg={8}>
            <Card>
              <CardHeader sx={{ padding: 2 }} title="Create snapshot for virtual rack" />
              <Divider />
              <CardContent sx={{ paddingTop: 1, maxHeight: '70vh', overflow: 'auto' }}>
                <Box sx={{ width: '100%', marginTop: '10px' }}>
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      This virtual rack will be powered off during the snapshot process.
                    </Alert>
                  </Grid>
                  <Grid container sx={{ marginTop: '30px' }}>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        required
                        id="snapshot-rack-name"
                        label="Virtual Rack Name"
                        placeholder="Virtual Rack Name"
                        InputLabelProps={{ shrink: true }}
                        value={newForm.rackName}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </Grid>
                  <Grid container sx={{ marginTop: '25px' }}>
                    <Grid item xs={12} sm={12}>
                      <SnapshotName
                        snapshotName={newForm.snapshotName}
                        updateSnapshotName={(name) =>
                          setNewForm((prev) => ({ ...prev, snapshotName: name }))
                        }
                        handleSnapshotNameValidationChange={handleSnapshotNameValidationChange}
                      />
                    </Grid>
                  </Grid>
                  <Grid container sx={{ marginTop: '20px' }}>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        required
                        id="snapshot-description"
                        label="Description"
                        value={newForm.description}
                        onChange={setField('description')}
                        fullWidth
                        helperText="Describe what this snapshot captures, why it was taken, and any other important information"
                        inputProps={{ 'data-testid': 'snapshot-description' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
              <Divider />
              <Box sx={{ padding: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Box>
                  <Button
                    data-testid="close-create-snapshot-modal"
                    color="secondary"
                    variant="contained"
                    endIcon={<CloseOutlinedIcon />}
                    onClick={closeHandler}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="create-snapshot-submit-btn"
                    sx={{ marginLeft: 2 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSubmit();
                    }}
                    color="primary"
                    variant="contained"
                    disabled={!formValid || !isSnapshotNameValid}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={1} lg={3} onClick={closeHandler} />
        </Grid>
      </Fade>
    </Modal>
  );
}
